import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Mail, Check, Send } from 'lucide-react';
import { superAdminAPI } from '../../services/api';

const AnalystModal = ({ isOpen, mode, analyst, onClose, onSave, onResendEmail }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'CONTENT_GENERATOR', // Default to content generator role
    enabled: true,
    sendWelcomeEmail: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [analystCreated, setAnalystCreated] = useState(false);
  const [createdAnalystEmail, setCreatedAnalystEmail] = useState('');

  // Use ref to track if we should preserve the success state
  const preserveSuccessState = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (analyst && (mode === 'edit' || mode === 'view')) {
        setFormData({
          name: analyst.name || '',
          email: analyst.email || '',
          role: analyst.roles?.includes('ROLE_CONTENT_SUPERVISOR') ? 'CONTENT_SUPERVISOR' :
                analyst.roles?.includes('ROLE_CONTENT_GENERATOR') ? 'CONTENT_GENERATOR' :
                analyst.roles?.includes('ROLE_CONTENT_REVIEWER') ? 'CONTENT_REVIEWER' : 'CONTENT_GENERATOR',
          enabled: analyst.enabled !== false,
          sendWelcomeEmail: false // Don't send email for existing users
        });
        setAnalystCreated(false); // Reset success state for edit/view modes
        setCreatedAnalystEmail(''); // Reset stored email
        preserveSuccessState.current = false;
      } else if (mode === 'add') {
        // Only reset form and success state when modal is first opened for add mode
        // Don't reset if we should preserve the success state
        if (!preserveSuccessState.current) {
          setFormData({
            name: '',
            email: '',
            role: 'CONTENT_GENERATOR',
            enabled: true,
            sendWelcomeEmail: true
          });
          setAnalystCreated(false);
        }
      }
      setErrors({});
    } else {
      // Reset success state when modal is closed
      setAnalystCreated(false);
      setCreatedAnalystEmail('');
      preserveSuccessState.current = false;
    }
  }, [analyst, mode, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      if (mode === 'add') {
        // Create user data based on selected role
        const userData = {
          name: formData.name,
          email: formData.email,
          sendWelcomeEmail: formData.sendWelcomeEmail
        };

        // Call appropriate API endpoint based on role
        switch (formData.role) {
          case 'CONTENT_SUPERVISOR':
            await superAdminAPI.createContentSupervisor(userData);
            break;
          case 'CONTENT_GENERATOR':
            await superAdminAPI.createContentGenerator(userData);
            break;
          case 'CONTENT_REVIEWER':
            await superAdminAPI.createContentReviewer(userData);
            break;
          default:
            throw new Error('Invalid role selected');
            break;
        }

        // Store the email before resetting form for success message display
        setCreatedAnalystEmail(formData.email);

        // Set a flag to show success message and preserve it
        setAnalystCreated(true);
        preserveSuccessState.current = true;

        // Call onSave to refresh the analyst list, but don't close the modal
        onSave && onSave(false); // false = don't close modal

        // Clear form errors on success
        setErrors({});

        // Reset form for potential next user creation
        setFormData({
          name: '',
          email: '',
          role: 'CONTENT_GENERATOR',
          enabled: true,
          sendWelcomeEmail: true
        });

      } else if (mode === 'edit') {
        const updateData = {
          name: formData.name,
          email: formData.email,
          enabled: formData.enabled
        };

        await superAdminAPI.updateAnalyst(analyst.id, updateData);

        // For edit mode, call onSave and close the modal
        onSave && onSave(true); // true = close modal
        onClose();
      }

    } catch (error) {
      console.error('Error saving analyst:', error);
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Failed to save analyst. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!analyst || !analyst.id) return;

    if (!window.confirm(`Resend welcome email to ${analyst.name}?\n\nThis will generate a new temporary password and send it to ${analyst.email}.`)) {
      return;
    }

    try {
      setLoading(true);

      await superAdminAPI.resendAnalystWelcomeEmail(analyst.id, {
        platformName: 'BayesPrep Platform'
      });

      alert(`Welcome email resent successfully to ${analyst.email}!\n\nA new temporary password has been generated and sent.`);

      if (onResendEmail) {
        onResendEmail(analyst);
      }

      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error resending welcome email:', error);
      const errorMessage = error.response?.data?.message || 'Failed to resend welcome email. Please try again.';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isViewMode = mode === 'view';

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'CONTENT_SUPERVISOR': return 'Content Supervisor';
      case 'CONTENT_GENERATOR': return 'Content Generator';
      case 'CONTENT_REVIEWER': return 'Content Reviewer';
      default: return 'Content User';
    }
  };

  const title = mode === 'add'
    ? `Add New ${getRoleDisplayName(formData.role)}`
    : mode === 'edit'
    ? `Edit ${getRoleDisplayName(formData.role)}`
    : `View ${getRoleDisplayName(formData.role)}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-responsive">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 p-4">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Analyst Creation Success Display */}
          {mode === 'add' && analystCreated && (
            <div className="bg-green-50 border border-green-200 p-4 mb-6">
              <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                <Check className="h-4 w-4 mr-2" />
                {getRoleDisplayName(formData.role)} Created Successfully!
              </h4>
              <div className="space-y-3">
                <p className="text-sm text-green-700">
                  The {getRoleDisplayName(formData.role).toLowerCase()} account has been created successfully.
                </p>
                {formData.sendWelcomeEmail ? (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                    <h5 className="text-sm font-medium text-blue-800 mb-1 flex items-center">
                      <Send className="h-4 w-4 mr-2" />
                      Secure Setup Email Sent
                    </h5>
                    <p className="text-sm text-blue-700">
                      A secure account setup link has been sent to <strong>{createdAnalystEmail}</strong>.
                    </p>
                    <ul className="text-xs text-blue-600 mt-2 space-y-1">
                      <li>• The analyst will receive a professional welcome email</li>
                      <li>• Setup link expires in 24 hours for security</li>
                      <li>• No passwords are sent via email</li>
                      <li>• Analyst will set their own secure password</li>
                      <li>• Analyst can access draft questions management</li>
                    </ul>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                    <p className="text-sm text-yellow-700">
                      Account created but no setup email was sent. You can manually provide the analyst with login instructions.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isViewMode}
                className={`input-field ${errors.name ? 'border-red-300' : ''}`}
                placeholder="e.g., Jane Smith"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className={`input-field pl-10 ${errors.email ? 'border-red-300' : ''}`}
                  placeholder="analyst@company.com"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Role Selection */}
          {mode === 'add' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="CONTENT_SUPERVISOR">Content Supervisor</option>
                <option value="CONTENT_GENERATOR">Content Generator</option>
                <option value="CONTENT_REVIEWER">Content Reviewer</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select the role for this user. Content roles have enhanced permissions and workflow features.
              </p>
            </div>
          )}

          {/* Role Information */}
          {(() => {
            const getRoleInfo = (role) => {
              switch (role) {
                case 'CONTENT_SUPERVISOR':
                  return {
                    title: 'Content Supervisor Permissions',
                    color: 'blue',
                    permissions: [
                      '• Full access to content management workflow',
                      '• Manage content teams and assignments',
                      '• Review and approve draft questions',
                      '• Publish questions to main collection',
                      '• Access to analytics and reporting',
                      '• Manage KIT assignments and organization'
                    ]
                  };
                case 'CONTENT_GENERATOR':
                  return {
                    title: 'Content Generator Permissions',
                    color: 'green',
                    permissions: [
                      '• Create and edit draft questions',
                      '• Upload questions via Excel bulk import',
                      '• Access assigned KITs and topics',
                      '• Submit questions for review',
                      '• View own question status and feedback',
                      '• Limited access to content portal'
                    ]
                  };
                case 'CONTENT_REVIEWER':
                  return {
                    title: 'Content Reviewer Permissions',
                    color: 'orange',
                    permissions: [
                      '• Review assigned draft questions',
                      '• Provide feedback and suggestions',
                      '• Approve or reject questions',
                      '• Access review queue and assignments',
                      '• View review analytics',
                      '• Cannot create or publish questions'
                    ]
                  };
                default:
                  return {
                    title: 'Unknown Role',
                    color: 'gray',
                    permissions: [
                      '• No permissions defined for this role'
                    ]
                  };
              }
            };

            const roleInfo = getRoleInfo(formData.role);
            const colorClasses = {
              blue: 'bg-blue-50 border-blue-200 text-blue-800 text-blue-700',
              green: 'bg-green-50 border-green-200 text-green-800 text-green-700',
              orange: 'bg-orange-50 border-orange-200 text-orange-800 text-orange-700',
              purple: 'bg-purple-50 border-purple-200 text-purple-800 text-purple-700'
            };

            return (
              <div className={`${colorClasses[roleInfo.color].split(' ').slice(0, 2).join(' ')} border p-4 rounded-lg`}>
                <h3 className={`text-sm font-medium ${colorClasses[roleInfo.color].split(' ')[2]} mb-2 flex items-center`}>
                  <FileText className="h-4 w-4 mr-2" />
                  {roleInfo.title}
                </h3>
                <ul className={`text-xs ${colorClasses[roleInfo.color].split(' ')[3]} space-y-1`}>
                  {roleInfo.permissions.map((permission, index) => (
                    <li key={index}>{permission}</li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Settings</h3>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="ml-2 text-sm text-gray-700">Account enabled</span>
              </label>

              {mode === 'add' && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="sendWelcomeEmail"
                    checked={formData.sendWelcomeEmail}
                    onChange={handleInputChange}
                    className="border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Send secure account setup email
                  </span>
                </label>
              )}
            </div>
          </div>

          {/* View mode additional info */}
          {isViewMode && analyst && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created At
                </label>
                <span className="text-sm text-gray-900">
                  {analyst.createdAt ? new Date(analyst.createdAt).toLocaleString() : 'N/A'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Login
                </label>
                <span className="text-sm text-gray-900">
                  {analyst.lastLoginAt ? new Date(analyst.lastLoginAt).toLocaleString() : 'Never'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Time Login
                </label>
                <span className={`text-sm ${analyst.isFirstTimeLogin ? 'text-orange-600' : 'text-green-600'}`}>
                  {analyst.isFirstTimeLogin ? 'Pending' : 'Completed'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Verified
                </label>
                <span className={`text-sm ${analyst.emailVerified ? 'text-green-600' : 'text-red-600'}`}>
                  {analyst.emailVerified ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-6 border-t">
            <div>
              {isViewMode && analyst && (
                <button
                  type="button"
                  onClick={handleResendEmail}
                  disabled={loading}
                  className="btn-secondary flex items-center disabled:opacity-50"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Sending...' : 'Resend Welcome Email'}
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              {/* Show different buttons based on whether analyst was created successfully */}
              {mode === 'add' && analystCreated ? (
                // Analyst was created successfully - show Create Another and Close buttons
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setAnalystCreated(false);
                      setCreatedAnalystEmail('');
                      preserveSuccessState.current = false;
                    }}
                    className="btn-secondary"
                  >
                    Create Another
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </>
              ) : (
                // Normal state - show Cancel/Close and Create/Update buttons
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                  >
                    {isViewMode ? 'Close' : 'Cancel'}
                  </button>
                  {!isViewMode && (
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary disabled:opacity-50"
                    >
                      {loading
                        ? (mode === 'add' ? `Creating ${getRoleDisplayName(formData.role).toLowerCase()}...` : `Updating ${getRoleDisplayName(formData.role).toLowerCase()}...`)
                        : mode === 'add' ? `Create ${getRoleDisplayName(formData.role)}` : `Update ${getRoleDisplayName(formData.role)}`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnalystModal;
